import React, { useState, useEffect, useCallback } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './components/MeetingApp.css';
import { configureAmplify } from "./services";
import WebPageHeader from './components/WebPageHeader';
import Sidebar from './components/Sidebar';
import WebPageMain from './components/WebPageMain';
import AIInsights from './components/AIInsights';
import WebPageFooter from './components/WebPageFooter';
import { generateClient } from 'aws-amplify/api';
import { listItems, getItem } from './graphql/queries';
import { deleteItem } from './graphql/mutations';
import { requestMeetingSummary } from './graphql/mutations';
import { onModifyItem, onDeleteItem, onRequestMeetingSummary } from './graphql/subscriptions';
import { fetchAuthSession } from 'aws-amplify/auth';
configureAmplify();

const client = generateClient();

function App() {
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const [isSummaryGenerating, setIsSummaryGenerating] = useState(false);

    const [identityId, setIdentityId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const [currentPage, setCurrentPage] = useState('meetings'); // 'meetings' or 'insights'
    
    const fetchItems = useCallback(async () => {
        if (!identityId) return;
        
        try {
            setIsFetching(true);
            const query = listItems;
            const variables = { 
                limit: 1000,
                cognito_identity_id: identityId
            };
            
            if (selectedDate) {
                // Filter by selected date
                const startOfDay = new Date(selectedDate);
                startOfDay.setHours(0, 0, 0, 0);
                const endOfDay = new Date(selectedDate);
                endOfDay.setHours(23, 59, 59, 999);
                
                variables.dateFilter = 'custom';
                variables.startDate = startOfDay.toISOString();
                variables.endDate = endOfDay.toISOString();
            }
            
            const response = await client.graphql({
                query: query,
                variables: variables
            });
            const rawItems = response.data.listItems.items;
            setItems(rawItems);
            setNextToken(response.data.listItems.nextToken);
        } catch (error) {
            console.error('Error fetching items:', error);
        } finally {
            setIsFetching(false);
        }
    }, [selectedDate, identityId]);

    useEffect(() => {
        // Fetch the identity ID when the component mounts
        const fetchIdentityId = async () => {
            try {
                const credentials = await fetchAuthSession();
                setIdentityId(credentials.identityId);
            } catch (error) {
                console.error('Error fetching identity ID:', error);
            }
        };
        fetchIdentityId();

        fetchItems();

        const intervalId = setInterval(() => {
            fetchItems();
        }, 10*1000);

        const subscriptions = [
            client.graphql({ query: onModifyItem }).subscribe({
                next: ({ data }) => {
                    console.log('onModifyItem:', data);
                    const modifiedItem = data.onModifyItem;
                    setItems(prevItems => {
                        const index = prevItems.findIndex(item => item.key === modifiedItem.key);
                        if (index !== -1) {
                            const newItems = [...prevItems];
                            newItems[index] = modifiedItem;
                            return newItems;
                        }
                        return [modifiedItem, ...prevItems];
                    });
                    if (selectedItem && selectedItem.key === modifiedItem.key) {
                        setSelectedItem(modifiedItem);
                    }
                },
                error: error => console.error(error)
            }),
            client.graphql({ query: onDeleteItem }).subscribe({
                next: ({ data }) => {
                    if (data.onDeleteItem.success) {
                        setItems(prevItems => prevItems.filter(item => item.key !== data.onDeleteItem.key));
                    }
                },
                error: error => console.error(error)
            }),
            // ... keep other subscriptions (onDeleteItems, onRequestMeetingSummary) ...
            client.graphql({ query: onRequestMeetingSummary }).subscribe({
                next: ({ data }) => {
                    console.log('Meeting summary requested:', data.onRequestMeetingSummary);
                    fetchItems();
                },
                error: error => console.error(error)
            }),
        ];

        return () => {
            clearInterval(intervalId);
            subscriptions.forEach(subscription => subscription.unsubscribe());
        };
    }, [fetchItems, selectedItem]);

    const handleItemSelect = (item) => {
        console.log('Selected item:', item);
        setSelectedItem(item);
    };

    const handleDeleteItem = async (item) => {
        if (!item.key || !item.bucket || !item.object_name) {
            console.error('Error: key, bucket, and object_name are required');
            return;
        }
        try {
            const response = await client.graphql({
                query: deleteItem,
                variables: {
                    cognito_identity_id: item.cognito_identity_id,
                    object_name: item.object_name,
                    bucket: item.bucket,
                    key: item.key
                }
            });
            if (response.data.deleteItem.success) {
                setItems(prevItems => prevItems.filter(i => i.key !== item.key));
            } else {
                console.error('Error deleting item');
            }
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const handleRequestMeetingSummary = async (item) => {
        if (!item.object_name || !item.cognito_identity_id) {
            console.error('Error: object_name and cognito_identity_id are required');
            return;
        }
        try {
            setIsSummaryGenerating(true);
            const response = await client.graphql({
                query: requestMeetingSummary,
                variables: { cognito_identity_id: item.cognito_identity_id, object_name: item.object_name }
            });
            console.log('Meeting summary requested:', response.data.requestMeetingSummary);
            // Start polling for updates
            pollForSummaryUpdate(item);
        } catch (error) {
            console.error('Error requesting meeting summary:', error);
            setIsSummaryGenerating(false);
        }
    };

    const pollForSummaryUpdate = async (item) => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await client.graphql({
                    query: getItem,
                    variables: { cognito_identity_id: item.cognito_identity_id, object_name: item.object_name }
                });
                const updatedItem = response.data.getItem;
                if (updatedItem.meeting_summary) {
                    clearInterval(pollInterval);
                    setSelectedItem(updatedItem);
                    setIsSummaryGenerating(false);
                }
            } catch (error) {
                console.error('Error polling for summary update:', error);
                clearInterval(pollInterval);
                setIsSummaryGenerating(false);
            }
        }, 5000); // Poll every 5 seconds
    };

    const handleUploadComplete = useCallback(async () => {
        console.log('Upload completed, fetching updated items');
        await fetchItems();
    }, [fetchItems]);



    const loadMoreItems = () => {
        if (nextToken && !isFetching) {
            fetchItems();
        }
    };

    return (
        <Authenticator hideSignUp={true}>
            {({ signOut, user }) => (
                <div
                    style={{
                        height: '100vh',
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr auto',
                        gridTemplateColumns: currentPage === 'insights' ? '1fr' : (sidebarOpen ? '280px 1fr' : '1fr')
                    }}
                    className="meeting-app"
                >
                    <div style={{ gridColumn: '1 / -1' }}>
                        <WebPageHeader
                            user={user}
                            onSignOut={signOut}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                            sidebarOpen={sidebarOpen}
                        />
                    </div>

                    {currentPage === 'insights' ? (
                        <AIInsights />
                    ) : (
                        <>
                            {sidebarOpen && (
                                <Sidebar
                                    items={items}
                                    onUploadComplete={handleUploadComplete}
                                    onItemSelect={handleItemSelect}
                                    hasMoreItems={!!nextToken}
                                    onLoadMore={loadMoreItems}
                                    identityId={identityId}
                                    selectedItem={selectedItem}
                                    onDeleteItem={handleDeleteItem}
                                    selectedDate={selectedDate}
                                    onDateChange={setSelectedDate}
                                />
                            )}

                            <WebPageMain
                                selectedItem={selectedItem}
                                onRequestMeetingSummary={handleRequestMeetingSummary}
                                isSummaryGenerating={isSummaryGenerating}
                            />
                        </>
                    )}

                    <div style={{ gridColumn: '1 / -1' }}>
                        <WebPageFooter />
                    </div>
                </div>
            )}
        </Authenticator>
    );
}

export default App;
